import { Artwork_Bool_Exp } from '../../../schema/commonSchemaRemoteTypes';
import {
  AppliedListingModifier,
  COLLECTION_LOCAL_CONDITION,
  COLLECTION_LOCAL_CONDITION_PAYLOAD,
  COLLECTION_LOCAL_SORT,
  COLLECTION_LOCAL_SORT_PAYLOAD,
  COLLECTION_QUERY_CONDITION,
  COLLECTION_QUERY_CONDITION_IN,
  BaseOptionValueType,
  FILTERS_DEFAULT_VALUE,
  isOptionType,
} from '../config/types';

type AppliedFilters = {
  artworkQueryCondition: Artwork_Bool_Exp;
  localSort: COLLECTION_LOCAL_SORT_PAYLOAD;
  localFilter: COLLECTION_LOCAL_CONDITION_PAYLOAD;
};
export const concatAppliedListModifiers = (
  appliedListingModifiers: AppliedListingModifier[],
  extraFilters?: Artwork_Bool_Exp[]
): AppliedFilters => ({
  artworkQueryCondition: appliedListingModifiers.reduce<Artwork_Bool_Exp>(
    (appliedCondition, currentModifier) => {
      const flattenedOptions = currentModifier.flattenSelectedOptions(
        currentModifier.allOptions,
        currentModifier.selectedOptions
      );
      if (
        !isOptionType<COLLECTION_QUERY_CONDITION>(flattenedOptions, 'COLLECTION_QUERY_CONDITION') &&
        !isOptionType<COLLECTION_QUERY_CONDITION_IN>(flattenedOptions, 'COLLECTION_QUERY_CONDITION_IN')
      ) {
        return appliedCondition;
      }
      return {
        _and: [...(appliedCondition._and ?? []), flattenedOptions.payload],
      };
    },
    extraFilters?.length ? { _and: [...extraFilters] } : {}
  ),
  localFilter: appliedListingModifiers.reduce<COLLECTION_LOCAL_CONDITION_PAYLOAD>(
    (appliedLocalFilter, currentModifier) => {
      const flattenedOptions = currentModifier.flattenSelectedOptions(
        currentModifier.allOptions,
        currentModifier.selectedOptions
      );
      if (!isOptionType<COLLECTION_LOCAL_CONDITION>(flattenedOptions, 'COLLECTION_LOCAL_CONDITION')) {
        return appliedLocalFilter;
      }
      return (artwork) => appliedLocalFilter(artwork) && flattenedOptions.payload(artwork);
    },
    () => true
  ),
  localSort: appliedListingModifiers.reduce<COLLECTION_LOCAL_SORT_PAYLOAD>(
    (appliedLocalSort, currentModifier) => {
      const flattenedOptions = currentModifier.flattenSelectedOptions(
        currentModifier.allOptions,
        currentModifier.selectedOptions
      );
      if (!isOptionType<COLLECTION_LOCAL_SORT>(flattenedOptions, 'COLLECTION_LOCAL_SORT')) {
        return appliedLocalSort;
      }
      return (a, b) => appliedLocalSort(a, b) || flattenedOptions.payload(a, b);
    },
    () => 0
  ),
});

export const selectOptionFromAppliedModifier = (
  appliedListingModifier: AppliedListingModifier,
  optionValue: BaseOptionValueType
): BaseOptionValueType[] => {
  if (optionValue === FILTERS_DEFAULT_VALUE) return [FILTERS_DEFAULT_VALUE];
  const type = appliedListingModifier.selectionType;
  const { selectedOptions } = appliedListingModifier;
  const isAtIndex = selectedOptions.indexOf(optionValue);
  const usedOptions = [...selectedOptions];

  if (isAtIndex > -1) {
    usedOptions.splice(isAtIndex, 1);
    if (!usedOptions.length) return [FILTERS_DEFAULT_VALUE];
    return usedOptions;
  }

  if (type === 'SINGLE') {
    return [optionValue];
  }

  return [...usedOptions.filter((x) => x !== FILTERS_DEFAULT_VALUE), optionValue];
};
